import omit from 'lodash/omit';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import React from 'react';
import 'rxjs/add/observable/merge';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/take';
import 'rxjs/add/operator/takeUntil';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';
import {updateHammer} from '../rc-hammerjs/Hammer';
import {splitObject, stopPropagation} from './utils';


// https://github.com/hammerjs/hammer.js/issues/930
// https://github.com/JedWatson/react-hammerjs/issues/14
// require('hammerjs') when in a browser. This is safe because Hammer is only
// invoked in componentDidMount, which is not executed on the server.
const Hammer = (typeof window !== 'undefined') ? require('hammerjs') : undefined;

const options = {
  touchAction: 'auto',
  recognizers: {
    pinch: {enable: false},
    press: {enable: false},
    rotate: {enable: false},
    tap: {enable: false},
  }
};

export class Swipeout extends React.Component {
  static propTypes = {
    prefixCls: PropTypes.string,
    autoClose: PropTypes.bool,
    disabled: PropTypes.bool,
    left: PropTypes.arrayOf(PropTypes.object),
    right: PropTypes.arrayOf(PropTypes.object),
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    children: PropTypes.any,
  };

  static defaultProps = {
    prefixCls: 'rc-swipeout',
    autoClose: false,
    disabled: false,
    left: [],
    right: [],
    onOpen() {
    },
    onClose() {
    },
  };

  constructor(props) {
    super(props);

    this.state = {
      contentStyle: {
        left: 0,
      },
      coverStyle: {
        left: 0,
      },
    };

    this.close = this.close.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.onCloseSwipe = this.onCloseSwipe.bind(this);
    this.handleClickContent = this.handleClickContent.bind(this);
    this.onPanStart = this.onPanStart.bind(this);
    this.handlePanStart = this.handlePanStart.bind(this);
    this.onPan = this.onPan.bind(this);
    this.handlePan = this.handlePan.bind(this);
    this.onPanEnd = this.onPanEnd.bind(this);
    this.handlePanEnd = this.handlePanEnd.bind(this);
    this.setContentRef = this.setContentRef.bind(this);

    this.openedLeft = false;
    this.openedRight = false;

    this.hammerProps = {
      options: options,
      direction: 'DIRECTION_HORIZONTAL',
      onPanStart: this.handlePanStart,
      onPan: this.handlePan,
      onPanEnd: this.handlePanEnd,
    };
  }

  componentDidMount() {
    this.panStart$ = new Subject();
    this.pan$ = new Subject();
    this.panEnd$ = new Subject();
    this.close$ = new Subject();

    this.panStartSubscription = this.panStart$
      .subscribe(this.onPanStart);

    // Only allow panning after a start was successfully registered
    this.panEndSubscription = this.panStart$
      .switchMap(() =>
        this.pan$
          .takeUntil(this.buildPanEnd())
      )
      .subscribe(this.onPan);

    this.panEndSubscription = this.panStart$
      .switchMap(() => this.buildPanEnd())
      .subscribe(this.onPanEnd);

    this.closeSubscription = this.close$
      .debounceTime(250)
      .subscribe(this.close);
  }

  buildPanEnd() {
    return Observable
      .merge(
        this.pan$
        // Guarantee that even if we only get a pan-start, with no pan events, we still perform cleanup
          .startWith(null)
          .debounceTime(500),
        this.panEnd$,
      )
      .take(1);
  }

  componentWillUnmount() {
    if (this.onCloseSwipeSubscription) {
      document.body.removeEventListener('mousedown', this.onCloseSwipe);
      document.body.removeEventListener('touchstart', this.onCloseSwipe);
    }

    this.panStart$ = null;
    this.pan$ = null;
    this.panEnd$ = null;
    this.panStartSubscription && this.panStartSubscription.unsubscribe();
    this.panEndSubscription && this.panEndSubscription.unsubscribe();
    this.closeSubscription && this.closeSubscription.unsubscribe();

    if (this.hammer) {
      this.hammer.stop();
      this.hammer.destroy();
    }
    this.hammer = null;
  }

  // componentDidUpdate(prevProps) {
  //   if (this.hammer && prevProps.options !== this.props.options) {
  //     updateHammer(this.hammer, this.props);
  //   }
  // }

  handleClose(e) {
    if (this.close$) {
      this.close$.next(e);
    }
  }

  handlePanStart(e) {
    if (this.panStart$) {
      this.panStart$.next(e);
    }
  }

  handlePan(e) {
    if (this.pan$) {
      this.pan$.next(e);
    }
  }

  handlePanEnd(e) {
    if (this.panEnd$) {
      this.panEnd$.next(e);
    }
  }

  onCloseSwipe(ev) {
    if (this.openedLeft || this.openedRight) {
      const pNode = (node => {
        while (node.parentNode && node.parentNode !== document.body) {
          if (node.className.indexOf(`${this.props.prefixCls}-actions`) > -1) {
            return node;
          }
          node = node.parentNode;
        }
      })(ev.target);
      if (!pNode) {
        ev.preventDefault();
        // console.log(`onCloseSwipe => calling this.close()`);
        this.handleClose();
      }
    }
  }

  onPanStart(e) {
    // console.log('onPanStart(e)', this.props.disabled, e);
    if (this.props.disabled) {
      return;
    }
    const {left, right} = this.props;
    const width = this.content.offsetWidth;

    this.contentWidth = width;
    this.btnsLeftWidth = (width / 5) * left.length;
    this.btnsRightWidth = (width / 5) * right.length;
    this.panStartX = e.deltaX;
  }

  onPan(e) {
    // console.log('onPan(e)', this.props.disabled, e);
    if (this.props.disabled) {
      return;
    }
    const {left, right} = this.props;
    const posX = e.deltaX - this.panStartX;
    if (posX < 0 && right.length) {
      this._setStyle(Math.min(posX, 0));
    } else if (posX > 0 && left.length) {
      this._setStyle(Math.max(posX, 0));
    }
  }

  onPanEnd(e) {
    // console.log('onPanEnd(e)', this.props.disabled, e);
    if (this.props.disabled) {
      return;
    }

    const {left, right} = this.props;
    const posX = e.deltaX - this.panStartX;
    const contentWidth = this.contentWidth;
    const btnsLeftWidth = this.btnsLeftWidth;
    const btnsRightWidth = this.btnsRightWidth;
    const openX = contentWidth * 0.33;
    const openLeft = posX > openX || posX > btnsLeftWidth / 2;
    const openRight = posX < -openX || posX < -btnsRightWidth / 2;

    if (openRight && posX < 0 && right.length) {
      this.open(-btnsRightWidth, false, true);
    } else if (openLeft && posX > 0 && left.length) {
      this.open(btnsLeftWidth, true, false);
    } else {
      // console.log(`onPanEnd => calling this.close()`);
      this.handleClose();
    }
    // console.log(`onPanEnd => openedLeft:${this.openedLeft} || openedRight:${this.openedRight}`);
  }

  // left & right button click
  onBtnClick(ev, btn) {
    const onClick = btn.onClick;
    if (onClick) {
      onClick(ev);
    }
    if (this.props.autoClose) {
      // console.log(`onBtnClick[${btn}] => calling this.close()`);
      this.handleClose();
    }
  }

  setContentRef(el) {
    // Just in case this API gets called during cleanup
    if (!el) {
      if (this.hammer) {
        this.hammer.stop();
        this.hammer.destroy();
        this.hammer = null;
      }
      this.content = el;
      return;
    }

    // initialize or update the hammer js instance.
    if (!this.hammer) {
      this.hammer = new Hammer(el);
    } else if (this.content !== el) {
      this.hammer.stop();
      this.hammer.destroy();
      this.hammer = new Hammer(el);
    } else {
      // reference hasn't changed. Do nothing.
      return;
    }

    updateHammer(this.hammer, this.hammerProps);
    this.content = el;
  }

  handleClickContent(e) {
    // console.log(`handleClickContent(e) openedLeft:${this.openedLeft} || openedRight:${this.openedRight}`, e);
    if (this.openedLeft || this.openedRight) {
      e.stopPropagation();
      // console.log(`handleClickContent => calling this.close()`);
      this.handleClose();
    }
  }

  _getContentEasing(value, limit) {
    // limit content style left when value > actions width
    if (value < 0 && value < limit) {
      return limit - Math.pow(limit - value, 0.85);
    } else if (value > 0 && value > limit) {
      return limit + Math.pow(value - limit, 0.85);
    } else {
      return value;
    }
  }

  // set content & actions style
  _setStyle(value) {
    if (!this.content) {
      return;
    }

    const nextState = {};
    const {left, right} = this.props;
    const width = this.content.offsetWidth;

    this.contentWidth = width;
    this.btnsLeftWidth = (width / 5) * left.length;
    this.btnsRightWidth = (width / 5) * right.length;

    const limit = value === 0 ? 0 : (value > 0 ? this.btnsLeftWidth : -this.btnsRightWidth);
    const contentLeft = this._getContentEasing(value, limit);

    nextState.contentStyle = {
      left: `${contentLeft}px`,
    };
    nextState.coverStyle = {
      display: Math.abs(value) > 0 ? 'block' : 'none',
      width: `${width}px`,
      left: `${contentLeft}px`,
    };

    if (left.length) {
      const leftWidth = Math.max(Math.min(value, Math.abs(limit)), 0);
      nextState.leftStyle = {
        width: `${leftWidth}px`,
      };
    }
    if (right.length) {

      const rightWidth = Math.max(Math.min(-value, Math.abs(limit)), 0);
      nextState.rightStyle = {
        width: `${rightWidth}px`,
      };
    }

    this.setState(nextState);
  }

  open(value, openedLeft, openedRight) {
    // console.log(`open(value:${value}, openedLeft:${openedLeft}, openedRight:${openedRight})`);
    if (!this.openedLeft && !this.openedRight) {
      this.props.onOpen();
    }

    this.openedLeft = openedLeft;
    this.openedRight = openedRight;
    this._setStyle(value);

    if (!this.onCloseSwipeSubscription) {
      document.body.addEventListener('mousedown', this.onCloseSwipe, true);
      document.body.addEventListener('touchstart', this.onCloseSwipe, true);
      this.onCloseSwipeSubscription = true;
    }
  }

  close() {
    // console.log(`close()`);
    if (this.openedLeft || this.openedRight) {
      this.props.onClose();
    }
    this._setStyle(0);
    this.openedLeft = false;
    this.openedRight = false;

    if (this.onCloseSwipeSubscription) {
      document.body.addEventListener('mousedown', this.onCloseSwipe, true);
      document.body.addEventListener('touchstart', this.onCloseSwipe, true);
      this.onCloseSwipeSubscription = false;
    }
  }

  renderButtons(buttons, name, style) {
    const prefixCls = this.props.prefixCls;

    return (buttons && buttons.length) ? (
      <div
        className={`${prefixCls}-actions ${prefixCls}-actions-${name}`}
        style={style}
        onClick={stopPropagation}
      >
        {buttons.map((btn, i) => {
          return (
            <div
              key={i}
              className={`${prefixCls}-btn ${btn.hasOwnProperty('className') ? btn.className : ''}`}
              style={btn.style}
              role="button"
              onClick={(e) => this.onBtnClick(e, btn)}
            >
              {btn.button ? btn.button : (
                <div className={`${prefixCls}-text`}>
                  {btn.text || 'Click'}
                </div>
              )}
            </div>
          );
        })}
      </div>
    ) : null;
  }

  render() {
    const [{prefixCls, left, right, children}, restProps] = splitObject(
      this.props,
      ['prefixCls', 'left', 'right', 'children']
    );
    const divProps = omit(restProps, [
      'disabled',
      'autoClose',
      'onOpen',
      'onClose',
    ]);
    return (left.length || right.length) ? (
      <div className={`${prefixCls}`} {...divProps}>
        {/* 保证 body touchStart 后不触发 pan */}
        <div
          className={`${prefixCls}-cover`}
          style={this.state.coverStyle}
          onClick={this.handleClickContent}
        />
        {this.renderButtons(left, 'left', this.state.leftStyle)}
        {this.renderButtons(right, 'right', this.state.rightStyle)}
        <div className={`${prefixCls}-content`} ref={this.setContentRef} style={this.state.contentStyle}>
          {children}
        </div>
      </div>
    ) : (
      <div ref={this.setContentRef} {...divProps}>{children}</div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/js/_libs/rc-swipeout/index.js